import { useState, useEffect, useContext } from 'react';
import { FaChevronRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import defaultAvatar from '../../images/pngs/seller.png';
import Loader from '../../Components/utils/Loader';
import { LoginContext } from '../../Contexts/LoginContext';
import { formatCurrency, formatDate, calculateSellerAmount, orderSellerFeePercent } from '../../data/constants';
import DocumentModal from '../../Components/utils/DocumentModal';
import Logo from '../../images/pngs/Logo.png';
import SellerOrdersCalendar from '../../Components/seller/SellerOrdersCalendar';

const OrdersPage = ({ isSeller }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expenseStatementModalOpen, setExpenseStatementModalOpen] = useState(false);
  const navigate = useNavigate();
  const { feesObj, currency, exchangeRate } = useContext(LoginContext);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders${isSeller ? '/seller/current' : '/buyer/current'}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrders(response.data.orders);
        await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/notifications/read`, { role: isSeller ? 'seller' : 'buyer' }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrders();
  }, [isSeller]);

  const tabStatusMap = {
    all: [],
    active: ['Active', 'Out for Delivery', 'Ready for Pickup', 'Delivered', 'Picked Up'],
    pastDue: ['Past Due'],
    resolution: ['InDispute', 'OnHold'],
    completed: ['Completed', 'Resolved'],
    cancelled: ['Cancelled'],
  };

  const getFilteredOrders = () => {
    const statuses = tabStatusMap[activeTab];
    if (activeTab === 'all') {
      return orders;
    }
    const filteredOrders = orders.filter(order => statuses.includes(order.currentStatus));
    return filteredOrders && filteredOrders.length > 0 ? filteredOrders : [];
  };

  const getStatusStyle = (status) => {
    const statusStyles = {
      'Active': 'ordersStatus ordersStatusActive',
      'Out for Delivery': 'ordersStatus ordersStatusActive',
      'Ready for Pickup': 'ordersStatus ordersStatusActive',
      'Delivered': 'ordersStatus ordersStatusActive',
      'Picked Up': 'ordersStatus ordersStatusActive',
      'Past Due': 'ordersStatus ordersStatusPastDue',
      'InDispute': 'ordersStatus ordersStatusNeedResolution',
      'OnHold': 'ordersStatus ordersStatusNeedResolution',
      'Completed': 'ordersStatus ordersStatusCompleted',
      'Resolved': 'ordersStatus ordersStatusCompleted',
      'Cancelled': 'ordersStatus ordersStatusCancelled',
    };
    return statusStyles[status] || 'ordersStatus';
  };

  const navigateTo = (order) => {
    const route = isSeller ? `/sellerOrderDetailPage/${order._id}` : `/orderDetailPage/${order._id}`;
    navigate(route, { state: { isSeller } });
  };

  const amountToGet = (summary) => {
    const orderAmount = (summary.price * summary.quantity) + summary.deliveryFee;
    return calculateSellerAmount(orderAmount, orderSellerFeePercent(summary, feesObj.seller)).toFixed(2);
  };

  return (
    <div className="ordersPage">
      <section className="mainSection">
        {isSeller && (
          <SellerOrdersCalendar
            orders={orders}
            isLoading={isLoading}
            currency={currency}
            exchangeRate={exchangeRate}
            amountToGet={amountToGet}
            onSelectOrder={navigateTo}
          />
        )}
        <div className="tabsContainer" style={{ maxWidth: '1000px' }}>
          <button
            className={activeTab === 'all' ? 'activeTab' : 'tab'}
            onClick={() => setActiveTab('all')}
          >
            All
          </button>
          <button
            className={activeTab === 'active' ? 'activeTab' : 'tab'}
            onClick={() => setActiveTab('active')}
          >
            Active
          </button>
          <button
            className={activeTab === 'pastDue' ? 'activeTab' : 'tab'}
            onClick={() => setActiveTab('pastDue')}
          >
            Past Due
          </button>
          <button
            className={activeTab === 'resolution' ? 'activeTab' : 'tab'}
            onClick={() => setActiveTab('resolution')}
          >
            InDispute
          </button>
          <button
            className={activeTab === 'completed' ? 'activeTab' : 'tab'}
            onClick={() => setActiveTab('completed')}
          >
            Completed
          </button>
          <button
            className={activeTab === 'cancelled' ? 'activeTab' : 'tab'}
            onClick={() => setActiveTab('cancelled')}
          >
            Cancelled
          </button>
        </div>

        <div className="ordersTableWrapper">
          <table className="ordersMainTable">
            <thead>
              <tr className="ordersTableHeader">
                <th>{isSeller ? "Buyer" : "Seller"}</th>
                <th>Posting</th>
                <th>Order #</th>
                <th>Order placed on</th>
                <th>{isSeller ? "Amount to Get" : "Amount Paid"}</th>
                <th>Status</th>
                {!isSeller && (
                  <th className="ordersTableActionsHeader">
                    <button type="button" className="ordersTableExpenseStatementBtn" onClick={() => setExpenseStatementModalOpen(true)} title="Generate Expense Statement">
                      Expense Statement
                    </button>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                    <Loader type="simpleMini" />
                  </td>
                </tr>
              ) : (
                getFilteredOrders()?.length > 0 ? getFilteredOrders().map((order) => (
                  <tr key={order._id} className="ordersTableRow">
                    <td>
                      <div className="ordersSellerCell">
                        {!isSeller && <img
                          src={(order.sellerId?.profileImage || defaultAvatar)}
                          alt="Error"
                          className="ordersSellerAvatar"
                        />}
                        <span className="ordersSellerName singleLineSmallText">
                          {isSeller ? order.buyerInfo.fullName : (order.sellerId?.displayName || order.sellerId?.companyName || 'Seller')}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="ordersSellerCell">
                        <img
                          src={order.productInfo.thumbnail}
                          alt={order.productInfo.thumbnail || "Error"}
                          className="ordersProductAvatar"
                        />
                        <span className="ordersProductName singleLineSmallText">
                          {order.productInfo.title}
                          {order.isB2B ? ' (B2B)' : ''}
                          {order.productInfo?.listingType === 'service' ? ' (Service)' : ''}
                        </span>
                      </div>
                    </td>
                    <td>{order.customOrderId}</td>
                    <td>{formatDate(order.createdAt) || new Date(order.createdAt).toLocaleDateString()}</td>
                    <td>{isSeller ? formatCurrency(amountToGet(order.summary), currency, exchangeRate) : order.summary.depositAmount ? formatCurrency(order.summary.depositAmount, currency, exchangeRate) : formatCurrency(order.summary.total, currency, exchangeRate)}</td>
                    <td>
                      <span className={getStatusStyle(order.currentStatus)}>
                        {order.currentStatus}
                      </span>
                    </td>
                    <td>
                      <button
                        className="ordersViewDetailsButton"
                        onClick={() => navigateTo(order)}
                      >
                        View Details <FaChevronRight size={12} />
                      </button>
                    </td>
                  </tr>
                ))
                  : <div style={{ marginTop: '20px' }}>Nothing to show here</div>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {expenseStatementModalOpen && (
        <DocumentModal
          isOpen={expenseStatementModalOpen}
          onClose={() => setExpenseStatementModalOpen(false)}
          documentType="expenseStatement"
          logoPath={Logo}
        />
      )}
    </div>
  );
};

export default OrdersPage;